/**
 * What to tell a gardener when a request fails: the server's own sentence
 * where it gave one worth reading, or ours.
 *
 * A failed request carries two messages. Axios's ("Request failed with
 * status code 500") is about the wire and helps nobody holding a trowel. The
 * server's, when it bothered to write one for a refusal ("That picture is
 * larger than 5 MB. Try a smaller one."), is exactly the sentence worth
 * showing. Several screens put the first on screen because it was the one
 * to hand; this picks the second, or a plain fallback.
 *
 * The server writes its sentences in English only. Each refusal meant for a
 * gardener also carries a name for itself (`code`, or on older routes
 * `error`, such as PHOTO_TOO_LARGE), and any value its sentence mentions as a
 * field of its own (`limitMb: 5`). The sentence shown is the dictionary's
 * `serverErrors.<CODE>` in the reader's language, with those values put in;
 * a code the dictionary does not know yet shows the server's English, as
 * before.
 *
 * Only refusals (4xx) are trusted to speak for themselves. A 401 or 403
 * explains access rules to a developer ("Access denied - you can only upload
 * your own photo"), so those get the fallback. The server's request limit
 * (429, RATE_LIMITED) never speaks for itself: errorMessage answers its own
 * calm sentence with the support address (lib/serverFailure rateLimitedText,
 * B24), since the limit's old words said "try again". The AI allowance's 429
 * (AI_LIMIT) keeps its words.
 *
 * A failure on our side (a 500 or more, or no answer at all: offline, timed
 * out) is the standard (D31, lib/serverFailure): errorMessage reports it to
 * Sentry and answers the standard sentence, which says it has been reported
 * and names the support address, instead of the fallback's "Try again".
 * Draw the answer with FailureText (shared/components/ServerFailure) so that
 * address is a link.
 *
 * errorSentence is the same for a screen whose action goes through code that
 * throws a plain Error carrying a sentence already fit for the gardener (a
 * context that has said the refusal, or the standard sentence). It is not
 * what errorMessage does, since plain Errors elsewhere carry developer
 * wording ("Plant not found: 12") that must never reach the screen.
 *
 * Who uses it: most screens that catch a failed request (errorMessage with
 * a translated fallback, drawn with FailureText), api/photos (refusalWords on a 200 that says
 * success: false), and the profile's Links screen (refusalFields: a reason under each refused box). The sentences are serverErrors.<CODE> in the locale
 * files; a new refusal code wants a key there in both languages.
 *
 * @module errorMessage
 */

import axios from 'axios';
import { getLanguage, t } from '@/shared/i18n/i18nSetup';
import { rateLimitedText, serverFailureText } from '@/shared/lib/serverFailure';

/** Refusals whose wording is meant for developers, not gardeners. */
const DEVELOPER_STATUSES = new Set([401, 403]);

/** The shape of a refusal's name: PHOTO_TOO_LARGE, not a sentence that happens to sit in `error`. */
const REFUSAL_CODE = /^[A-Z][A-Z0-9_]*$/;

/** Fields of a refusal that are about the refusal itself, not values for its sentence. */
const NOT_VALUES = new Set(['success', 'code', 'error', 'message', 'defaultValue']);

/**
 * Values written as numbers in the reader's way (6,000 or 6.000), not as the
 * bare digits the server sends: pixel counts and the like, never a ZIP code.
 */
const NUMBER_VALUES = new Set(['maxEdge']);

/** A refusal's body as the backend sends it: a name, a sentence, and the values the sentence mentions. */
type RefusalBody = Record<string, unknown> & { code?: unknown; error?: unknown; message?: unknown };

/**
 * A refusal's sentence in the reader's language, from the body the server
 * answered with: the dictionary's words for its code, or the server's own
 * sentence when the dictionary does not know the code.
 *
 * @param data - The response body.
 * @returns The sentence, or null when the body names no refusal the dictionary
 *   knows and carries no sentence of its own.
 */
export function refusalWords(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const body = data as RefusalBody;
  const said = typeof body.message === 'string' && body.message.trim() ? body.message : null;
  const code = [body.code, body.error].find(
    (value): value is string => typeof value === 'string' && REFUSAL_CODE.test(value)
  );
  if (!code) return said;
  const values: Record<string, string | number> = {};
  for (const [name, value] of Object.entries(body)) {
    if (NOT_VALUES.has(name)) continue;
    if (typeof value === 'number' && NUMBER_VALUES.has(name)) values[name] = value.toLocaleString(getLanguage());
    else if (typeof value === 'string' || typeof value === 'number') values[name] = value;
  }
  // An empty default stands for "the dictionary has no such code", since t()
  // would otherwise answer with the key itself.
  const words = t(`serverErrors.${code}`, { ...values, defaultValue: said ?? '' });
  return words || null;
}

/**
 * The server's own explanation of a refusal, when it gave one meant for the
 * person who asked, in the reader's language where the dictionary has it.
 * For the server's request limit (a 429 other than AI_LIMIT) it is never
 * the server's words but the limit's own sentence (rateLimitedText).
 *
 * @param error - Whatever the request threw.
 * @returns The sentence, or null when there is none worth showing.
 */
export function serverMessage(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const limited = rateLimitedText(error);
  if (limited) return limited;
  const status = error.response?.status ?? 0;
  if (status < 400 || status >= 500 || DEVELOPER_STATUSES.has(status)) return null;
  return refusalWords(error.response?.data);
}

/**
 * A refusal's reasons per field, when the server gave one for each box it
 * refused (the profile links' 400 LINK_INVALID: { fields: { instagram: "…" } },
 * each already in the reader's language). Only a refusal (4xx) is read.
 *
 * @param error - Whatever the request threw.
 * @returns Each refused field's sentence, or null when the refusal names none.
 */
export function refusalFields(error: unknown): Record<string, string> | null {
  if (!axios.isAxiosError(error)) return null;
  const status = error.response?.status ?? 0;
  if (status < 400 || status >= 500 || DEVELOPER_STATUSES.has(status)) return null;
  const fields = (error.response?.data as { fields?: unknown } | undefined)?.fields;
  if (!fields || typeof fields !== 'object') return null;
  const out = Object.fromEntries(Object.entries(fields as Record<string, unknown>).filter((entry): entry is [string, string] => typeof entry[1] === 'string'));
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Whether a public page's request was answered "unavailable" (410): the
 * gardener behind it is suspended, so the page says only that.
 *
 * @param error - Whatever the request threw.
 * @returns True for a 410.
 */
export function isUnavailable(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 410;
}

/**
 * A sentence for a failed request: the standard sentence for a failure on
 * our side (reported to Sentry as it is chosen), the request limit's sentence
 * for a 429 (rateLimitedText), the server's refusal if it explained itself,
 * otherwise the fallback.
 *
 * @param error - Whatever the request threw.
 * @param fallback - What to say otherwise, already translated.
 * @returns Text to put on screen.
 */
export function errorMessage(error: unknown, fallback: string): string {
  return serverFailureText(error) ?? rateLimitedText(error) ?? serverMessage(error) ?? fallback;
}

/**
 * errorMessage, except that a plain Error's own message is kept: for an
 * action that throws an Error whose message is already the sentence for the
 * gardener (PlantsContext's onSave and onRemove). A request's own failure
 * goes through errorMessage as before, and a bug (a TypeError and the like,
 * any subclass of Error) gets the fallback, never its developer's message.
 *
 * @param error - Whatever the action threw.
 * @param fallback - What to say otherwise, already translated.
 * @returns Text to put on screen.
 */
export function errorSentence(error: unknown, fallback: string): string {
  if (error instanceof Error && error.constructor === Error && error.message) return error.message;
  return errorMessage(error, fallback);
}
